<template>
  <div class="ecc-widget-container">
    <div class="card border-0 shadow-sm" style="border-radius: 14px; background: #ffffff; border: 1.5px solid #e2e8f0 !important; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);">
      <!-- Standard Vertex Header -->
      <div class="card-header bg-white border-0 pt-3 pb-2 px-3 d-flex justify-content-between align-items-center flex-nowrap gap-2" style="border-bottom: 1px solid #f1f5f9 !important;">
        <div class="d-flex align-items-center gap-2" style="min-width: 0;">
          <div class="widget-icon-pill" style="background-color: #fef3c7; color: #f59e0b; width: 34px; height: 34px; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 0.95rem; flex-shrink: 0;">
            <i class="fas fa-shield-alt"></i>
          </div>
          <div class="text-truncate">
            <h6 class="mb-0 fw-bold text-truncate" style="color: #1e293b; font-size: 0.875rem;">Email Compromise Check (ECC)</h6>
            <small class="text-muted text-truncate d-block" style="color: #64748b; font-size: 0.72rem;">Email breaches and compromised accounts</small>
          </div>
        </div>
        <div
          class="widget-controls ecc-header-alerts flex-shrink-0 align-items-center gap-2"
          v-if="hasECC && !$helper.isVertexAnalyst() && ($helper.isUserAdmin() || $helper.settingsAdmin() || $store.state.auth.isSuperAdmin)"
        >
          <label class="mandatory-toggle-container m-0" v-tooltip.top="'Alert impacted users to new data breaches'">
            <input
              class="mandatory-toggle-input"
              type="checkbox"
              role="switch"
              id="eccHeaderAlertSwitch"
              v-model="alert"
              @change="saveOrganisationFeature('update')"
            />
            <span class="mandatory-toggle-track">
              <span class="mandatory-toggle-thumb"></span>
            </span>
            <span class="ms-2 mandatory-toggle-label">
              Email Alerts
            </span>
          </label>
        </div>
      </div>

      <div class="card-body p-3" v-if="hasECC">
        <!-- Modern Stat Cards Grid (Equal Heights) -->
        <div class="ecc-stat-grid mb-3">
          <div class="ecc-stat-card p-2 text-center">
            <div class="ecc-stat-label">Last Run</div>
            <div class="ecc-stat-value text-slate" :title="lastRun">{{ lastRun }}</div>
          </div>
          <div class="ecc-stat-card p-2 text-center">
            <div class="ecc-stat-label">Users Checked</div>
            <div class="ecc-stat-value text-slate">{{ usersChecked }}</div>
          </div>
          <div class="ecc-stat-card p-2 text-center">
            <div class="ecc-stat-label">Impacted Accounts</div>
            <div class="ecc-stat-value" :class="impactedAccounts > 0 ? 'text-danger' : 'text-success'">
              {{ impactedAccounts }}
            </div>
          </div>
        </div>

        <!-- Content Area: Loading / Empty / Table -->
        <div class="row">
          <div v-if="loading" class="col-md-12 text-center py-4">
            <div class="spinner-border spinner-border-sm text-warning mb-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="text-muted small mb-0">Email compromise check is in progress.</p>
          </div>

          <div class="col-12 text-center py-4" v-else-if="formattedCompromisedAccounts == null || formattedCompromisedAccounts.length == 0">
            <svg width="48" height="42" viewBox="0 0 57 50" fill="none" xmlns="http://www.w3.org/2000/svg" class="mb-2">
              <path d="M30.1829 41.2996C30.1829 40.3634 30.2927 39.4548 30.4299 38.5463H5.4878V11.0132L27.439 24.7797L49.3902 11.0132V25.0275C51.3659 25.3579 53.2043 26.0463 54.878 27.0099V5.50661C54.878 2.47797 52.4085 0 49.3902 0H5.4878C2.46951 0 0 2.47797 0 5.50661V38.5463C0 41.5749 2.46951 44.0529 5.4878 44.0529H30.4299C30.2927 43.1443 30.1829 42.2357 30.1829 41.2996ZM49.3902 5.50661L27.439 19.2731L5.4878 5.50661H49.3902ZM43.2165 50L35.6707 41.7401L38.8537 38.5463L43.2165 42.924L53.0671 33.0396L56.25 36.9218L43.2165 50Z" fill="#10b981"/>
            </svg>
            <p class="text-muted small mb-0">
              No accounts have been identified as impacted in a recently disclosed data breach.
            </p>
          </div>

          <div class="col-12 table-responsive" v-else>
            <table class="table mb-3 mt-1" style="table-layout: fixed; width: 100%;">
              <thead>
                <tr class="ecc-table-header">
                  <th class="ps-3" style="width: 32%">DATE & TIME</th>
                  <th style="width: 40%">USER ACCOUNT</th>
                  <th class="text-end pe-3" style="width: 28%"># OF BREACHES</th>
                </tr>
              </thead>
              <tbody class="align-middle" v-for="(item, index) in formattedCompromisedAccounts" :key="index">
                <tr @click="setExpandData(item)" data-bs-toggle="collapse" :data-bs-target="'#logs' + index" aria-expanded="false" aria-controls="logs" v-tooltip.top="'Click to show breach details'" style="cursor:pointer;" class="ecc-row">
                  <td class="fw-semibold text-slate text-truncate" :title="item.name">{{ item.name }}</td>
                  <td class="text-muted small text-truncate" :title="item.email">{{ item.email }}</td>
                  <td class="text-end">
                    <span class="badge-breach-count">{{ item.breach_count }}</span>
                  </td>
                </tr>
                <tr class="collapse collapse-vertical" :id="'logs'+index" style="transition: opacity ease-in-out;">
                  <td colspan="3" class="p-0 border-0">
                    <div class="p-2 bg-slate-subtle rounded-3 my-1">
                      <table class="table table-sm expand mb-0">
                        <thead style="white-space: nowrap;">
                          <tr class="text-muted small">
                            <th>Company Name</th>
                            <th>Breach Date</th>
                            <th>Compromised Data</th>
                            <th>Breach Summary</th>
                          </tr>
                        </thead>
                        <tbody v-if="!loading" class="text-slate">
                          <tr v-for="(item, compIndex) in expandedData.companies" :key="compIndex">
                            <td style="width: 30%; text-align:left;">
                              {{ item.company }}
                            </td>
                            <td style="width: 30%; text-align:left;">
                              {{ item.breach_date }}
                            </td>
                            <td style="width: 30%; text-align:left;">
                              {{ item.compromised_data }}
                            </td>
                            <td v-tooltip.top="'Show breach summary'" style="width: 10%; text-align:center;">
                              <button class="btn btn-sm btn-outline-secondary rounded-circle" @click="openSummaryDialog(item.summary)">
                                <i class="fa fa-link"></i>
                              </button>
                            </td>
                          </tr>
                        </tbody>
                        <tbody v-else>
                          <tr>
                            <td colspan="4" class="text-center py-2">
                              <div class="spinner-border spinner-border-sm" role="status"></div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Footer Controls Row -->
        <div class="ecc-footer-row mt-3 pt-3 border-top d-flex align-items-center justify-content-between flex-nowrap gap-2" style="border-color: #f1f5f9 !important;">
          <div class="d-flex align-items-center gap-2 flex-nowrap">
            <button
              type="button"
              class="btn-initiate-pill"
              aria-label="Run a manual check for new data breach alerts"
              v-tooltip.top="'Run a manual check for new data breach alerts'"
              @click="initiateCheck"
              :disabled="($helper.isVertexAnalyst() && !$helper.settingsAdmin() && !$store.state.auth.isSuperAdmin) || $store.state.auth.isGeneralUser || (!$store.state.auth.isECCAdmin && !$store.state.auth.isSuperAdmin) || loading"
            >
              <span v-if="!loading" class="initiate-icon"><i class="fas fa-sync-alt"></i></span>
              <span v-else class="spinner-border spinner-border-sm initiate-icon" role="status"></span>
              <span class="initiate-text">Initiate Check</span>
            </button>
            <button
              v-if="hasECC"
              type="button"
              class="btn-download-pill"
              aria-label="Download ECC Summary (.xlsx)"
              v-tooltip.top="'Download ECC Summary (.xlsx)'"
              @click="download"
              :disabled="downloading"
            >
              <div v-if="downloading" class="spinner-border text-primary download-icon" role="status" style="height: 12px; width: 12px;">
                <span class="visually-hidden">Loading...</span>
              </div>
              <svg v-if="!downloading" class="download-icon" width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
                <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
              </svg>
              <span class="download-text">Download</span>
            </button>
          </div>
          <div
            class="ecc-footer-alerts align-items-center"
            v-if="hasECC && !$helper.isVertexAnalyst() && ($helper.isUserAdmin() || $helper.settingsAdmin() || $store.state.auth.isSuperAdmin)"
          >
            <label class="mandatory-toggle-container m-0" v-tooltip.top="'Alert impacted users to new data breaches'">
              <input
                class="mandatory-toggle-input"
                type="checkbox"
                role="switch"
                id="eccFooterAlertSwitch"
                v-model="alert"
                @change="saveOrganisationFeature('update')"
              />
              <span class="mandatory-toggle-track">
                <span class="mandatory-toggle-thumb"></span>
              </span>
              <span class="ms-2 mandatory-toggle-label">
                Email Alerts
              </span>
            </label>
          </div>
        </div>
      </div>

      <!-- Empty / Disabled Body State -->
      <div class="card-body p-4 text-center" v-else>
        <div class="py-4">
          <i class="fas fa-shield-alt text-muted mb-2" style="font-size: 2rem;"></i>
          <h6 class="fw-bold text-dark mb-1">No Data Available</h6>
          <p class="text-muted small mb-0">Email Compromise Check is not enabled or no records exist.</p>
        </div>
      </div>
    </div>

    <!-- Modal Dialog -->
    <vue-final-modal v-model="summaryDialog" classes="modal-container" content-class="modal-content">
      <span class="modal__title">Breach Summary</span>
      <div class="modal__content mt-3 mb-4">
        <div>
          <div class="card">
            <div class="card-body">
              <p v-html="sanitizedSummaryData"></p>
            </div>
          </div>
        </div>
      </div>
      <div class="modal__action">
        <button type="button" class="btn btn-outline-danger" @click="closeSummary">Close</button>
      </div>
    </vue-final-modal>
  </div>
</template>

 <script>
	import { VueScreenSizeMixin } from 'vue-screen-size';
  import moment from 'moment';
  import * as XLSX from 'xlsx'

   export default {
    name: 'Dashboard',
    props: ['organisationID', 'isManager', 'showProduct'],
		mixins: [VueScreenSizeMixin],

    setup() {
      return {
        moment: moment,
      };
    },

    data() {
      return {
        summaryDialog: false,
        summaryData: null,
        hasECC: false,
        checking: false,
        alert: true,
        expand: false,
        loading: false,
        headers: [
          { text: "Name", value: "name" },
          { text: "Email", value: "email" },
          { text: "# of breaches identified", value: "breach_count" }
        ],
        compromisedAccounts: [],
        latestCompromisedAccounts: [],
        formattedCompromisedAccounts: [],
        ecc: {},
        eccLatestRun: {},
        btnInitiate: "#00A0C1",
        btnDownload: "#00A0C1",
        expandedData: [],
        vssWidth: 3000,
        shouldHideAlerts: false,
        viewHistory: false,
        lastThreeBreach: {},
        downloading: false,
        impactedAccounts: null,
      };
      },

    async created() {
      if (!this.showProduct) return

      this.init()
      this.vssWidth = this.$vssWidth;
    },

     methods: {

      init(){
        if (this.$store.state.auth.isSuperAdmin || this.$helper.isVertexAnalyst() || this.isManager){
          this.getData()
          this.hasECC = true

          if (this.isManager && this.$store.state.auth.isGeneralUser)  return

          this.checkEnabledFeatures()
        }
        else {
          let products = this.$store.state.user.organisation.products
          products.forEach(v => {
            if (v.product == 'email_compromise_check')
              this.hasECC = true
          });

          if (this.hasECC){
            this.getData()
            this.checkEnabledFeatures()
          }
        }
      },

      setExpandData(item) {
        this.expandedData = item
      },

      setCompromisedAccounts(data) {
        let emails = []
        let compromisedAccounts = data

        data.forEach(value => {

          if ( !emails.includes(value.email) ) {

            let filteredAccounts = this.filterAccountsByEmail(compromisedAccounts, value.email)
            let count = 0

            let tempData = {
              name : value.name,
              email : value.email,
              breach_count: 0,
              companies: []
            }

            for (const account of filteredAccounts) {
              count++
              tempData.companies.push({
                company: account.company,
                breach_date: account.breach_date,
                compromised_data: account.compromised_data,
                summary: account.description,
              })
            }

            tempData.breach_count = count

            this.formattedCompromisedAccounts.push(tempData)
            emails.push(value.email)
          }
        });

        if (this.isManager) {
          this.impactedAccounts = emails.length
        }

      },

      filterAccountsByEmail(array, email) {
        return array.filter((value) => {
              return value.email == email
            });
      },

      async getData() {
        await this.getLastRun()

        if (this.ecc != null)
          await this.getLatestCompromisedAccounts(this.ecc.ID)
      },

      async getLastRun() {
        let params = {};

        if (this.isManager)  params.manager = this.isManager;

        try {
          const response = await this.$axios.get(`/orgadmin/email-compromise-check/organisations/${this.organisationID}/last-run`, {params});

          if (response.data.success) {
              this.ecc = response.data.email_compromise_check;
              this.eccLatestRun = response.data.email_compromise_check;
            }
        } catch (error) {
          // Handle error
        }
      },

      async getCompromisedAccounts() {
        let params = {};

        if (this.isManager)  params.manager = this.isManager;
        await this.$axios.get(`/orgadmin/email-compromise-check/organisations/${this.organisationID}/compromised-account`,{params})
          .then(  response => {
            this.compromisedAccounts = response.data.compromised_accounts
          })
          .catch()
      },

      async getLatestCompromisedAccounts(id) {
        let params = {};

        if (this.isManager)  params.manager = this.isManager;

        await this.$axios.get(`/orgadmin/email-compromise-check/organisations/${this.organisationID}/compromised-account/${id}`,{params})

          .then(  response => {
            this.latestCompromisedAccounts = response.data.compromised_accounts
            this.lastThreeBreach = this.sortLatest(this.latestCompromisedAccounts)
            this.setCompromisedAccounts(response.data.compromised_accounts)
            this.tableLoading = false
          })
          .catch()
      },

      sortLatest(accounts) {
        // Convert the object to an array for easier sorting
        const accountsArray = Object.values(accounts);

        // Sort the array by 'CreatedAt' in descending order using 'moment' for date comparison
        const sortedArray = accountsArray.sort((a, b) => {
          return moment(b.CreatedAt).diff(moment(a.CreatedAt));
        });

        // Get the latest 3 items
        return sortedArray.slice(0, 3);
      },

      async initiateCheck() {
        this.loading = true
        this.formattedCompromisedAccounts = []

        let data = await this.$axios.get(`/orgadmin/email-compromise-check/organisations/${this.organisationID}/check-pawned-accounts`)
          .then(  response => {
              if (response.data.success) {
                return response.data.ecc
              } else {
                this.$toast.error(response.data.message)
                this.loading = false
                return 0
              }
          })
          .catch(() => {
            this.loading = false
            return 0
          })

        // get last run
        await this.getLastRun()

        // get data after initiating a check
        if (data.ecc_id > 0) {
          await this.getLatestCompromisedAccounts(data.ecc_id)
          this.viewHistory = false;
        }

        this.loading = false;
        this.btnInitiate = '#00A0C1';
      },

      async getOrganisationFeatures() {
      let data = await this.$axios.get(`/orgadmin/organisations/${this.organisationID}/organisation-feature`)
          .then((response) => {
              if (response.data.success){
                  return response.data.org_features
              } else {
                  this.$toast.error(response.data.message)
                  return null
              }
          })
          .catch()

          return data
      },

      saveOrganisationFeature(state){
        let data = {
            organisation_id: this.organisationID,
            email_compromise_check: this.alert,
        }

        if ( state == 'save') {
          this.$axios.post(`/orgadmin/organisations/`+ this.organisationID +`/organisation-feature`, data)
          .catch();
          } else {
            this.$axios.put(`/orgadmin/organisations/`+ this.organisationID +`/organisation-feature`, data)
            .then((response) => {
              if (!response.data.success)
                  this.$toast.success(response.data.message)
              if (this.alert)
                this.$toast.success('Email Compromise alerts have been enabled for all users in your organisation.')
              else
                this.$toast.success('Email Compromise alerts have been disabled for all users in your organisation.')
              })
            .catch()
        }
      },

      async checkEnabledFeatures() {
        const orgFeatures = await this.getOrganisationFeatures();

        if ( orgFeatures == null || orgFeatures.length < 1 ) {
            this.saveOrganisationFeature('save')
        } else {
          if (orgFeatures[0].email_compromise_check == null) {
            this.saveOrganisationFeature('update')
            return
          }
          this.alert = orgFeatures[0].email_compromise_check
        }
      },

      sanitizeSpreadsheetCell(val) {
        if (typeof val !== 'string') return val ?? ''
        if (/^[=+\-@\t\r]/.test(val)) {
          return `'${val}`
        }
        return val
      },

      async download() {
        //set download state
        this.downloading = true;
        await this.getCompromisedAccounts()

        if (this.compromisedAccounts.length == 0){
          this.$toast.error('No compromised accounts to download.')
          this.downloading = false;
          return
        }

        var dateToday = moment().format("YYYYMMD")

        let data = await this.prepareData();

        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
        /* generate file and send to client */
        XLSX.writeFile(wb, dateToday + "_" + this.formattedOrgName + "_Email_Compromise_Check_Report.xlsx");

        //remove download state
        this.downloading = false;
        this.btnDownload = '#00A0C1';
      },

      async prepareData() {
        let rows = [["Employee Name", "Email", "Breach Date", "Breach Organisation", "Priority", "Compromised Data", "Breach Summary", "Date Notified of Breach",]];
        for (const account of (this.compromisedAccounts || [])) {
          rows.push([
            this.sanitizeSpreadsheetCell(account.name),
            this.sanitizeSpreadsheetCell(account.email),
            this.sanitizeSpreadsheetCell(account.breach_date),
            this.sanitizeSpreadsheetCell(account.company),
            this.checkDataPriority(account.compromised_data),
            this.sanitizeSpreadsheetCell(account.compromised_data),
            this.sanitizeSpreadsheetCell(account.description),
            moment(account.CreatedAt).local().format("h:mma D MMM YYYY")
          ]);
        }

        return rows
      },

      closeSummary(){
        this.summaryDialog = false
        this.summaryData =  null
      },

      openSummaryDialog(item){
        this.summaryDialog = true
        this.summaryData =  item
      },

      checkDataPriority(data) {
        const highPriority = ["Auth tokens", "Biometric data", "Credit cards", "Dates of birth", "Driver's licenses", "Encrypted keys", "Government issued IDs", "Passport numbers", "Passwords", "Personal health data", "PINs", "Security questions and answers", "Social security numbers"];
        const mediumPriority = ["Appointments", "Deceased statuses", "Delivery instructions", "Device information", "Device usage tracking data", "Email messages", "Family members' names", "Flights taken", "Job applications", "Licence plates", "Marital statuses", "Mnemonic phrases", "Names", "Partial credit card data", "Partial dates of birth", "Payment methods", "Phone numbers", "Physical addresses", "Places of birth", "Spouses names", "Usernames", "Vehicle details"];
        const lowPriority = ["Age groups", "Ages", "Avatars", "Bios", "Browser user agent details", "Eating habits", "Education levels", "Email addresses", "Employers", "Employment statuses", "Ethnicities", "Family structure", "Genders", "Geographic locations", "Home ownership statuses", "Homepage URLs", "Income levels", "Instant messenger identities", "IP addresses", "Job titles", "Living costs", "MAC addresses", "Nationalities", "Occupations", "Partial phone numbers", "Password strengths", "Personal descriptions", "Physical attributes", "Professional skills", "Profile photos", "Religions", "Salutations", "Smoking habits", "Social media profiles", "Spoken languages", "Survey results", "Time zones", "User website URLs", "Website activity", "Years of professional experience", ""];

        const dataArr = data.split(',');

        for (let i = 0; i < highPriority.length; i++) {
          if (dataArr.includes(highPriority[i])) {
            return "High";
          }
        }

        for (let i = 0; i < mediumPriority.length; i++) {
          if (dataArr.includes(mediumPriority[i])) {
            return "Medium";
          }
        }

        for (let i = 0; i < lowPriority.length; i++) {
          if (dataArr.includes(lowPriority[i])) {
            return "Low";
          }
        }

        return "Unknown";
      },

      sanitizeHtml(html) {
        if (!html || typeof html !== 'string') return '';
        try {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          // Remove dangerous elements
          const dangerousTags = ['script', 'iframe', 'object', 'embed', 'link', 'style', 'base', 'form', 'input', 'button', 'applet', 'meta'];
          dangerousTags.forEach(tag => {
            const elements = doc.querySelectorAll(tag);
            elements.forEach(el => el.remove());
          });

          // Strip inline on* handlers and unsafe uri schemes
          const allElements = doc.querySelectorAll('*');
          allElements.forEach(el => {
            const attrs = Array.from(el.attributes);
            attrs.forEach(attr => {
              const attrName = attr.name.toLowerCase();
              const attrVal = attr.value.trim().toLowerCase();

              if (attrName.startsWith('on')) {
                el.removeAttribute(attr.name);
              } else if (attrVal.startsWith('javascript:') || attrVal.startsWith('vbscript:') || (attrVal.startsWith('data:') && !attrVal.startsWith('data:image/'))) {
                el.removeAttribute(attr.name);
              }
            });

            if (el.hasAttribute('href')) {
              const href = el.getAttribute('href').trim().toLowerCase();
              if (href.startsWith('javascript:') || href.startsWith('vbscript:') || href.startsWith('data:')) {
                el.removeAttribute('href');
              }
            }
            if (el.hasAttribute('src')) {
              const src = el.getAttribute('src').trim().toLowerCase();
              if (src.startsWith('javascript:') || src.startsWith('vbscript:')) {
                el.removeAttribute('src');
              }
            }
          });

          return doc.body.innerHTML;
        } catch (e) {
          console.error('HTML sanitization failed', e);
          return '';
        }
      },

      updateImpactedAccounts() {
        if (this.eccLatestRun == null) {
          this.impactedAccounts = null;
        } else if (this.eccLatestRun.ID === 0) {
          this.impactedAccounts = "0";
        } else {
          this.impactedAccounts = this.eccLatestRun.impacted_accounts;
        }
      }
    },

    watch: {
      eccLatestRun: {
        handler() {
          this.updateImpactedAccounts();
        },
        immediate: true,
        deep: true
      }
    },

    computed: {
      lastRun() {
        if (this.eccLatestRun == null)
          return null

        return this.eccLatestRun.ID == 0 ? "___________________" : moment(this.eccLatestRun.last_checked).local().format("h:mma D MMM YYYY");
      },

      usersChecked() {
        if (this.eccLatestRun == null)
          return null

        return this.eccLatestRun.ID == 0 ? "0" : this.eccLatestRun.users_checked;
      },

      orgName(){
        return this.$store.state.auth.isSuperAdmin || this.$helper.isVertexAnalyst() ? this.$store.state.user.organisation.name : this.$store.state.user.organisation.organisation
      },

      formattedOrgName() {
      if (this.orgName) {
        if (this.orgName.includes(" ")) {
          // If orgName contains spaces, replace them with underscores
          return this.orgName.replace(/ /g, "_");
        } else {
          // If orgName doesn't contain spaces, keep it as is
          return this.orgName;
        }
      }
      return "";
    },

      sanitizedSummaryData() {
        return this.sanitizeHtml(this.summaryData);
      },
    },

  }

 </script>
<style scoped>
.ecc-widget-container {
  container-type: inline-size;
  container-name: ecc-card;
}

.ecc-header-alerts {
  display: flex;
}

.ecc-footer-alerts {
  display: none;
}

.widget-icon-pill {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.text-slate {
  color: #1e293b;
  font-size: 0.8125rem;
}

.ecc-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  align-items: stretch;
}

.ecc-stat-card {
  background: transparent;
  border: none;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 44px;
  height: 100%;
}

.ecc-stat-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ecc-stat-value {
  font-size: 0.8125rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ecc-table-header th {
  background-color: #F4F6F8 !important;
  color: #435666;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 10px 12px;
  border-bottom: 1.5px solid #e2e8f0;
  white-space: nowrap;
}

.ecc-row td {
  padding: 11px 12px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.8125rem;
  transition: background-color 0.15s ease;
}

.ecc-row:hover td {
  background-color: #f8fafc;
}

.expand th {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748b;
}

.expand td {
  font-size: 0.8125rem;
}

.bg-slate-subtle {
  background-color: #f8fafc;
  border: 1px solid #f1f5f9;
}

.badge-breach-count {
  display: inline-block;
  background-color: #fef3c7;
  color: #d97706;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 9999px;
  min-width: 24px;
  text-align: center;
}

.border-top-subtle {
  border-top: 1px solid #f1f5f9;
}

.btn-download-pill {
  background-color: #ffffff;
  color: #00a0c1;
  border: 1.5px solid #00a0c1;
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.btn-download-pill:hover:not(:disabled) {
  background-color: #00a0c1;
  color: #ffffff;
  border-color: #00a0c1;
  box-shadow: 0 2px 6px rgba(0, 160, 193, 0.35);
}

.btn-download-pill:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-download-pill .download-icon {
  margin-right: 6px;
  flex-shrink: 0;
}

.btn-download-pill .download-text {
  font-weight: 700;
  font-size: 13px;
}

.btn-initiate-pill {
  background-color: #ffffff;
  color: #00a0c1;
  border: 1.5px solid #00a0c1;
  border-radius: 20px;
  padding: 4px 14px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-initiate-pill:hover:not(:disabled) {
  background-color: #00a0c1;
  color: #ffffff;
  border-color: #00a0c1;
  box-shadow: 0 2px 6px rgba(0, 160, 193, 0.35);
}

.btn-initiate-pill:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-initiate-pill .initiate-icon {
  margin-right: 6px;
  font-size: 12px;
  flex-shrink: 0;
}

.btn-initiate-pill .initiate-text {
  font-weight: 700;
  font-size: 13px;
}

@container ecc-card (max-width: 460px) {
  .ecc-header-alerts {
    display: none !important;
  }
  .ecc-footer-alerts {
    display: flex !important;
  }
  .ecc-stat-label {
    font-size: 0.65rem;
    letter-spacing: 0.02em;
  }
  .ecc-table-header th {
    padding: 10px 8px;
    font-size: 0.7rem;
  }
  .btn-initiate-pill,
  .btn-download-pill {
    padding: 6px 10px;
    min-width: 34px;
    height: 28px;
  }
  .btn-initiate-pill .initiate-text,
  .btn-download-pill .download-text {
    display: none !important;
  }
  .btn-initiate-pill .initiate-icon,
  .btn-download-pill .download-icon {
    margin-right: 0 !important;
  }
}

@media (max-width: 575.98px) {
  .ecc-header-alerts {
    display: none !important;
  }
  .ecc-footer-alerts {
    display: flex !important;
  }
  .btn-initiate-pill,
  .btn-download-pill {
    padding: 6px 10px;
    min-width: 34px;
    height: 28px;
  }
  .btn-initiate-pill .initiate-text,
  .btn-download-pill .download-text {
    display: none !important;
  }
  .btn-initiate-pill .initiate-icon,
  .btn-download-pill .download-icon {
    margin-right: 0 !important;
  }
}

.mandatory-toggle-container {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  margin: 0;
}

.mandatory-toggle-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  margin: 0;
  pointer-events: none;
}

.mandatory-toggle-track {
  position: relative;
  display: inline-block;
  width: 32px;
  height: 18px;
  background-color: #cbd5e1;
  border-radius: 18px;
  transition: background-color 0.25s ease, box-shadow 0.25s ease;
  flex-shrink: 0;
}

.mandatory-toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 14px;
  height: 14px;
  background-color: #ffffff;
  border-radius: 50%;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.mandatory-toggle-input:checked + .mandatory-toggle-track {
  background-color: #00a0c1;
}

.mandatory-toggle-input:checked + .mandatory-toggle-track .mandatory-toggle-thumb {
  transform: translateX(14px);
  background-color: #ffffff;
}

.mandatory-toggle-container:hover .mandatory-toggle-track {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
}

.mandatory-toggle-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.form-check-input:checked {
  background-color: #00a0c1;
  border-color: #00a0c1;
}

:deep(.modal-container) {
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.modal-content) {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 90%;
  margin: 0 1rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #fff;
  width: 30%;
}

:deep(.modal-content-monthly) {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 90%;
  margin: 0 1rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #fff;
  width: 30%;
}

.modal__action {
  display: flex;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 1rem 0 0;
}
</style>
